import CustomLoadingButton from 'Components/CustomLoadingButton'
import StyledLabel from 'Components/StyledLabel'
import { SELECTED_REFUND_CODE_PARAMETER_KEY } from 'Services/ControlDataManagement/Features/RefundCode/Hooks/useRefundCodesWithParams'
import {
  useCreateSingleRefundCodeMutation,
  useUpdateSingleRefundCodeMutation
} from 'Services/ControlDataManagement/Features/RefundCode/refundCodeApi'
import type { RefundCode } from 'Services/ControlDataManagement/Features/RefundCode/types'
import type { ControlDataServiceError } from 'Util/ApiClient'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamToUrl } from 'Util/url'
import { Box, FormControl, Grid, TextField } from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

interface RefundCodeFormProps {
  successBaseUrl: string
  submitLoading: boolean
  refundCode?: RefundCode
}

export default function RefundCodeForm({
  refundCode,
  successBaseUrl,
  submitLoading
}: Readonly<RefundCodeFormProps>) {
  const navigate = useNavigate()
  const isEdit = !!refundCode
  const [createSingleRefundCode, { isLoading: createLoading }] = useCreateSingleRefundCodeMutation()
  const [updateSingleRefundCode, { isLoading: updateLoading }] = useUpdateSingleRefundCodeMutation()
  const [refundCodeDetails, setRefundCodeDetails] = useState<Partial<RefundCode>>(
    (isEdit && refundCode) || {}
  )

  const handleChange = (
    value: string | string[] | number | boolean | undefined,
    field: keyof RefundCode
  ) => {
    if (value !== undefined) {
      setRefundCodeDetails(prevState => ({ ...prevState, [field]: value }) as RefundCode)
    }
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (refundCode) {
      updateSingleRefundCode({
        refund_code_id: refundCode.refund_code_id,
        ...refundCodeDetails
      })
        .unwrap()
        .then(() => {
          SnackbarUtils.success(t`Refund code updated successfully.`)
          navigate(successBaseUrl)
        })
        .catch(({ data: errorResponse }: { data: ControlDataServiceError }) => {
          const { message } = errorResponse
          if (message) {
            SnackbarUtils.error(message)
          } else {
            SnackbarUtils.error(t`There was an error updating the refund code.`)
          }
        })
      return
    }

    createSingleRefundCode(refundCodeDetails)
      .unwrap()
      .then((refundCodeResponse: RefundCode) => {
        SnackbarUtils.success(t`Refund code created successfully.`)
        navigate(
          appendQueryParamToUrl(
            successBaseUrl,
            SELECTED_REFUND_CODE_PARAMETER_KEY,
            refundCodeResponse.refund_code_id
          )
        )
      })
      .catch(({ data: errorResponse }: { data: ControlDataServiceError }) => {
        const { message } = errorResponse
        if (message) {
          SnackbarUtils.error(message)
        } else {
          SnackbarUtils.error(t`There was an error creating the refund code.`)
        }
      })
  }

  const isSubmitLoading = submitLoading || createLoading || updateLoading

  return (
    <Grid container spacing={2} mb={4}>
      <Grid size={12}>
        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', mb: 4 }}>
            <FormControl sx={{ minWidth: 240, mr: 3 }}>
              <TextField
                name="refund_code_id"
                required
                label={t`Refund code identifier`}
                size="small"
                value={refundCodeDetails?.refund_code_id ?? ''}
                slotProps={{ htmlInput: { maxLength: 40 } }}
                onChange={e => handleChange(e.target.value, 'refund_code_id')}
              />
            </FormControl>
          </Box>
          <StyledLabel>{t`Translations`}</StyledLabel>
          <Box sx={{ display: 'flex', mb: 4 }}>
            <FormControl sx={{ minWidth: 400, mr: 3 }}>
              <TextField
                name="explanation"
                required
                label={t`Explanation`}
                size="small"
                value={refundCodeDetails?.explanation ?? ''}
                slotProps={{ htmlInput: { maxLength: 500 } }}
                onChange={e => handleChange(e.target.value, 'explanation')}
                multiline={true}
                rows={4}
              />
            </FormControl>
          </Box>
          <CustomLoadingButton
            isLoading={isSubmitLoading}
            type="submit"
            size="large"
            iconPosition="end"
          >
            {t`Save refund code`}
          </CustomLoadingButton>
        </Box>
      </Grid>
    </Grid>
  )
}
